/**
 * Care Plans and Alerts (Stop and Watch), ported from the design's residentsPlansAndAlerts()
 * (L30851). The design's PLANS and ALERTS records are the rows of GET /care-plans and
 * GET /residents/stop-and-watch (both served), field for field.
 */
import type { CarePlanSummary, StopAndWatchAlert } from '@aithora/shared';
import { pre } from './dom';

type Ev = { preventDefault?: () => void } | undefined;
const INK = '#22272E', MUTE = '#54606C', BLUE = '#2F5C97', RED = '#B3261E', AMBER = '#5B6B7C', OK = '#1F6B52';
const TONE: Record<string, string> = { '': INK, red: RED, amber: AMBER, ok: OK, mute: MUTE, blue: BLUE };
const ease = 'cubic-bezier(.22,.61,.36,1)';
export const mark = (t: string) =>
  'flex:0 0 auto;display:inline-flex;align-items:center;height:16px;padding:0 6px;border-radius:3px;font-size:14px;font-weight:700;letter-spacing:.06em;white-space:nowrap;color:' +
  (TONE[t] || MUTE) + ';background:' + (TONE[t] || MUTE) + '1A;';
const bar = (pct: number, color: string, d: number) =>
  'display:block;height:100%;border-radius:2px;width:' + Math.max(0, Math.min(100, pct)) + '%;background:' + color + ';transform-origin:left;animation:growW .5s ' + ease + ' both;animation-delay:' + (d || 0) + 'ms;';
const chip = (label: string, n: number | string, on: boolean, color: string, fn: (e: Ev) => void) => ({
  label, n: String(n), onClick: fn,
  dot: 'width:6px;height:6px;border-radius:50%;display:inline-block;background:' + color + ';',
  style: 'display:inline-flex;align-items:center;gap:5px;height:26px;padding:0 10px;border-radius:13px;font-size:14px;font-weight:600;text-decoration:none;white-space:nowrap;transition:background .15s ' + ease + ';' +
    (on ? 'background:rgba(47,92,151,.13);color:#1D3F6B;' : 'background:rgba(34,39,46,.05);color:' + MUTE + ';'),
});

// ── Care plans: the 42 CFR 483.21 clock ─────────────────────────────────────
type Clock = { k: string; due: string; left: number | null; state: string; at: string | null };
function cfr(p: CarePlanSummary): Clock[] {
  const outs: Clock[] = [];
  outs.push(p.baselineDoneHours === null
    ? { k: 'Baseline', due: 'within 48h of admit', left: p.admitDaysAgo === null ? null : 48 - p.admitDaysAgo * 24, state: p.admitDaysAgo !== null && p.admitDaysAgo * 24 > 48 ? 'BREACH' : 'OPEN', at: null }
    : { k: 'Baseline', due: 'within 48h of admit', left: null, state: p.baselineDoneHours <= 48 ? 'MET' : 'LATE', at: p.baselineDoneHours + 'h' });
  outs.push(p.comprehensiveDoneDays === null
    ? { k: 'Comprehensive', due: 'within 21 days', left: p.admitDaysAgo === null ? null : (21 - p.admitDaysAgo) * 24, state: p.admitDaysAgo !== null && p.admitDaysAgo > 21 ? 'BREACH' : 'OPEN', at: null }
    : { k: 'Comprehensive', due: 'within 21 days', left: null, state: p.comprehensiveDoneDays <= 21 ? 'MET' : 'LATE', at: p.comprehensiveDoneDays + 'd' });
  outs.push(p.lastRevisionDaysAgo === null
    ? { k: 'Quarterly revision', due: 'every 90 days', left: null, state: 'NONE', at: null }
    : { k: 'Quarterly revision', due: 'every 90 days', left: (90 - p.lastRevisionDaysAgo) * 24, state: p.lastRevisionDaysAgo > 90 ? 'BREACH' : 90 - p.lastRevisionDaysAgo <= 14 ? 'DUE' : 'OK', at: p.lastRevisionDaysAgo + 'd ago' });
  return outs;
}
function fmtClock(c: Clock, urgency?: boolean): string {
  if (c.state === 'NONE') return 'not started';
  if (urgency && c.left !== null) return c.left > 24 ? Math.round(c.left / 24) + 'd left' : c.left > 0 ? Math.max(1, Math.round(c.left)) + 'h left' : 'overdue';
  if (c.at) return c.at;
  if (c.left === null) return 'not assessed';
  return c.left > 24 ? Math.round(c.left / 24) + 'd left' : c.left > 0 ? Math.max(1, Math.round(c.left)) + 'h left' : 'overdue';
}
const worstOf = (p: CarePlanSummary) => {
  const c = cfr(p);
  if (c.some((x) => x.state === 'BREACH')) return 'BREACH';
  if (c.some((x) => x.state === 'NONE')) return 'NO PLAN';
  if (c.some((x) => x.state === 'DUE' || x.state === 'OPEN')) return 'DUE';
  return 'CURRENT';
};
const rankOf = (p: CarePlanSummary) => { const w = worstOf(p); return w === 'BREACH' ? 0 : w === 'NO PLAN' ? 1 : w === 'DUE' ? 2 : 3; };
const W_TONE: Record<string, string> = { BREACH: 'red', 'NO PLAN': 'red', DUE: 'amber', CURRENT: 'ok' };
const W_MARK: Record<string, string> = { BREACH: '▲', 'NO PLAN': '○', DUE: '◆', CURRENT: '●' };
const sparkT = (pts: number[], tgt: number, w: number, h: number) => {
  const all = pts.concat([tgt]);
  const mn = Math.min(...all), mx = Math.max(...all), rg = mx - mn || 1;
  return pts.map((v, i) => (i ? 'L' : 'M') + (2 + (i / Math.max(1, pts.length - 1)) * (w - 4)).toFixed(1) + ' ' + (h - 3 - ((v - mn) / rg) * (h - 8)).toFixed(1)).join(' ');
};
const targetLine = (pts: number[], tgt: number, w: number, h: number) => {
  const all = pts.concat([tgt]);
  const mn = Math.min(...all), mx = Math.max(...all), rg = mx - mn || 1;
  const y = (h - 3 - ((tgt - mn) / rg) * (h - 8)).toFixed(1);
  return 'M2 ' + y + ' L' + (w - 2) + ' ' + y;
};

export interface PlansUi {
  sel: string | null;
  filter: string | null;
  goalDone: Record<string, boolean>;
  signed: Record<string, boolean>;
}

export function plansVals(opts: {
  plans: CarePlanSummary[];
  ui: PlansUi;
  setUi: (p: Partial<PlansUi>) => void;
  canWrite: boolean;
  markMet: (p: CarePlanSummary, goalId: string) => void;
  sign: (p: CarePlanSummary) => void;
  openChart: (residentId: string) => void;
}) {
  const { plans, ui, setUi } = opts;
  const ordered = plans.slice().sort((a, b) => rankOf(a) - rankOf(b));
  const FILTER_KEY: Record<string, string> = { Breached: 'BREACH', 'No plan': 'NO PLAN', Due: 'DUE', Current: 'CURRENT' };
  const CP = plans.find((p) => p.residentId === ui.sel) ?? ordered[0] ?? null;
  const gd = (p: CarePlanSummary, g: CarePlanSummary['goals'][number]) => g.evaluation === 'MET' || !!ui.goalDone[p.residentId + ':' + g.id];
  const base = {
    cpGate: '● 42 CFR 483.21',
    cpGateStyle: mark(plans.some((p) => worstOf(p) === 'BREACH') ? 'red' : 'ok'),
    cpLede: 'Baseline within 48 hours, comprehensive within 21 days, revised every quarter. Ranked by whichever clock runs out first.',
    cpChips: ([['Breached', RED, 'BREACH'], ['No plan', MUTE, 'NO PLAN'], ['Due', AMBER, 'DUE'], ['Current', OK, 'CURRENT']] as const).map((k) =>
      chip(k[0], plans.filter((p) => worstOf(p) === k[2]).length, ui.filter === k[0], k[1], (e) => { pre(e); setUi({ filter: ui.filter === k[0] ? null : k[0], sel: null }); })),
    cpList: ordered
      .filter((p) => !ui.filter || worstOf(p) === FILTER_KEY[ui.filter])
      .map((p) => {
        const w = worstOf(p);
        const c = cfr(p);
        const soon = c.filter((x) => x.state !== 'NONE' && x.left !== null).sort((a, b) => (a.left as number) - (b.left as number))[0] || c.filter((x) => x.state === 'BREACH')[0] || null;
        const m = p.goals.filter((g) => gd(p, g)).length;
        const on = CP?.residentId === p.residentId;
        return {
          name: p.name + ' · ' + (p.room ?? '–'),
          dx: p.diagnosis,
          due: w === 'NO PLAN' ? 'no plan yet' : soon ? fmtClock(soon, true) : 'current',
          dueColor: w === 'BREACH' || w === 'NO PLAN' ? RED : w === 'DUE' ? AMBER : MUTE,
          prog: p.goals.length ? m + '/' + p.goals.length : 'no goals',
          pips: (p.goals.length ? p.goals : [null, null, null]).map((g, i) =>
            'display:block;width:14px;height:4px;border-radius:2px;background:' +
            (!g ? 'rgba(34,39,46,.12)' : gd(p, g) ? OK : g.evaluation === 'PARTLY MET' ? AMBER : RED) + ';transform-origin:left;animation:growW .4s ' + ease + ' both;animation-delay:' + i * 60 + 'ms;'),
          onClick: (e: Ev) => { pre(e); setUi({ sel: p.residentId }); },
          rowStyle: 'display:block;min-width:0;padding:9px 0 9px 9px;border-left:2px solid ' + (on ? BLUE : TONE[W_TONE[w]] || 'rgba(34,39,46,.14)') +
            ';border-top:1px solid rgba(34,39,46,.12);text-decoration:none;color:' + (on ? BLUE : INK) + ';transition:border-color .15s ' + ease + ';',
        };
      }),
  };
  if (!CP) {
    return { ...base, cpTitle: 'No care plans in scope', cpState: '', cpStateStyle: 'display:none;', cpMeta: '', cpDx: '–', cpHasGoals: false, cpNoGoals: false, cpGoals: [], cpMet: '0', cpTotal: '0', cpRing: 'display:none;', cpHole: '', cpRingNum: '', cpBand: '', cpBandStyle: 'display:none;', cpBandNote: '', cpReviewLabel: '', cpReviewStyle: 'display:none;', cpReview: () => {}, cpReviewHint: '', cpEmptyActs: [], cpCanEdit: false, cpChg: '', cpChgShow: false };
  }
  const clocks = cfr(CP);
  const goals = CP.goals;
  const met = goals.filter((g) => gd(CP, g)).length;
  const signed = !!ui.signed[CP.residentId];
  const w = worstOf(CP);
  return {
    ...base,
    cpTitle: CP.name + ' · room ' + (CP.room ?? '–'),
    cpState: W_MARK[w] + ' ' + w,
    cpStateStyle: mark(W_TONE[w]),
    cpMeta: clocks.map((c) => c.k.split(' ')[0] + ' ' + fmtClock(c)).join(' · '),
    cpDx: CP.diagnosis,
    cpHasGoals: goals.length > 0,
    cpNoGoals: goals.length === 0,
    cpEmptyTitle: CP.baselineDoneHours === null ? 'No baseline care plan yet.' : 'No comprehensive plan yet.',
    cpEmptyNote: CP.baselineDoneHours === null
      ? (CP.admitDaysAgo === null ? 'No admission date is on file.' : 'Admitted ' + Math.round(CP.admitDaysAgo) + (CP.admitDaysAgo === 1 ? ' day' : ' days') + ' ago.') + ' The baseline plan is due within 48 hours of admission and nothing has been recorded: that is not-yet-assessed, not none.'
      : 'The baseline was done at ' + CP.baselineDoneHours + 'h. The comprehensive plan is due within 21 days of admission and has not been started. Billing gates on an active plan, so nothing here is claimable yet.',
    // V9: read-only roles (Super Admin, Support, Owner/Operator) get no Start the plan, no signing.
    cpCanEdit: opts.canWrite,
    // "Changed today because..." (V9 handoff.js): no route says why a plan changed yet.
    cpChg: '',
    cpChgShow: false,
    cpEmptyActs: ([['Start the plan', BLUE], ['Open the chart', null]] as const).filter((a) => opts.canWrite || a[0] !== 'Start the plan').map((a) => ({
      label: a[0],
      onClick: (e: Ev) => { pre(e); opts.openChart(CP.residentId); },
      style: 'display:inline-flex;align-items:center;height:30px;padding:0 13px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;' +
        (a[1] ? 'background:' + BLUE + ';color:#FCFAF4;' : 'background:rgba(47,92,151,.10);color:#1D3F6B;'),
    })),
    cpGoals: goals.map((g) => {
      const done = !!ui.goalDone[CP.residentId + ':' + g.id];
      const ev = done ? 'MET' : g.evaluation;
      const good = ev === 'MET';
      const inverse = g.target < g.value && g.unit === 'days';
      const drift = g.value - g.target;
      const acts: Array<[string, string | null]> = good ? [['Goal met', null]] : [['Mark met', 'met'], ['Revise it', 'revise']];
      return {
        name: g.name,
        evalLabel: ev,
        evalStyle: mark(ev === 'MET' ? 'ok' : ev === 'PARTLY MET' ? 'amber' : 'red'),
        val: String(g.value),
        unit: g.unit,
        valColor: good ? OK : ev === 'PARTLY MET' ? AMBER : RED,
        trend: sparkT(g.trend, g.target, 120, 26),
        target: targetLine(g.trend, g.target, 120, 26),
        trendC: good ? OK : ev === 'PARTLY MET' ? AMBER : RED,
        targetLabel: 'target ' + g.target + ' ' + g.unit,
        drift: good ? 'on target' : (inverse ? '+' : drift > 0 ? '+' : '') + drift.toFixed(drift % 1 ? 1 : 0) + ' off',
        driftColor: good ? OK : ev === 'PARTLY MET' ? AMBER : RED,
        how: g.assist + ' · ' + g.measure + (g.dueDays === null ? '' : g.dueDays ? ' · due in ' + g.dueDays + 'd' : ' · overdue'),
        evalNote: g.note,
        acts: acts.map((a) => ({
          label: a[0],
          onClick: (e: Ev) => {
            pre(e);
            if (a[1] === 'met' && opts.canWrite) opts.markMet(CP, g.id);
            else if (a[1] === 'revise') opts.openChart(CP.residentId);
          },
          style: 'display:inline-flex;align-items:center;height:27px;padding:0 11px;border-radius:6px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;' +
            (a[1] === 'met' ? 'background:' + BLUE + ';color:#FCFAF4;' : a[1] ? 'background:rgba(47,92,151,.10);color:#1D3F6B;' : 'background:rgba(31,107,82,.11);color:' + OK + ';'),
        })),
      };
    }),
    cpMet: String(met),
    cpTotal: String(goals.length),
    cpRing: 'flex:0 0 56px;width:56px;height:56px;border-radius:50%;display:flex;align-items:center;justify-content:center;background:conic-gradient(' +
      (goals.length && met === goals.length ? OK : AMBER) + ' ' + Math.round((goals.length ? met / goals.length : 0) * 360) +
      'deg, rgba(34,39,46,.10) 0);transition:background .4s ' + ease + ';animation:popIn .4s ' + ease + ' both;',
    cpHole: 'width:42px;height:42px;border-radius:50%;background:#F6F3EC;display:flex;flex-direction:column;align-items:center;justify-content:center;font-style:normal;',
    cpRingNum: 'display:block;font-size:17px;font-weight:700;letter-spacing:-.02em;font-style:normal;line-height:1;color:' + (goals.length && met === goals.length ? OK : AMBER) + ';',
    cpBand: !goals.length ? '○ NONE' : met === goals.length ? '● ON TRACK' : '◆ BEHIND',
    cpBandStyle: mark(!goals.length ? 'mute' : met === goals.length ? 'ok' : 'amber'),
    cpBandNote: !goals.length ? 'No goals to measure against yet.' : met === goals.length ? 'Every goal is being met on its own measure.' : goals.length - met + ' of ' + goals.length + ' behind target. Each one names what would move it.',
    cpReviewLabel: signed ? 'Review signed' : goals.length ? 'Sign the quarterly revision' : 'Start a plan',
    cpReviewStyle: 'flex:0 0 auto;display:inline-flex;align-items:center;height:30px;padding:0 14px;border-radius:7px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;transition:background .2s ' + ease + ';' +
      (signed ? 'background:rgba(31,107,82,.12);color:' + OK + ';' : 'background:' + BLUE + ';color:#FCFAF4;'),
    cpReview: (e: Ev) => {
      pre(e);
      if (signed || !opts.canWrite) return;
      if (goals.length) opts.sign(CP);
      else opts.openChart(CP.residentId);
    },
    cpReviewHint: signed
      ? 'Signed under your name and sealed. The 90-day clock restarts today.'
      : goals.length ? 'Signing records that you read every goal against its own measure, and restarts the 90-day clock.' : 'A plan needs at least one goal with a measure and a due date before it can be signed.',
  };
}

// ── Alerts: Stop and Watch ──────────────────────────────────────────────────
export interface AlertsUi {
  sel: string | null;
  filter: string | null;
  evOpen: boolean;
  sbarFor: string | null;
  acked: Record<string, boolean>;
  sent: Record<string, boolean>;
}
const sumOf = (a: number[]) => a.reduce((n, v) => n + v, 0);
const noisyOf = (a: StopAndWatchAlert) => sumOf(a.fired14) >= 5 && sumOf(a.acted14) <= 1;

export function alertsVals(opts: {
  alerts: StopAndWatchAlert[];
  ui: AlertsUi;
  setUi: (p: Partial<AlertsUi>) => void;
  canWrite: boolean;
  /** The read-only seat's name (V9 "Read-only as Super Admin"). */
  roleLabel?: string;
  ack: (a: StopAndWatchAlert) => void;
  send: (a: StopAndWatchAlert, sbar: Array<{ k: string; v: string }>) => void;
  notUseful: (a: StopAndWatchAlert) => void;
}) {
  const { alerts, ui, setUi } = opts;
  const isAck = (a: StopAndWatchAlert) => a.acknowledged || !!ui.acked[a.residentId];
  const isSent = (a: StopAndWatchAlert) => a.sbarSent || !!ui.sent[a.residentId];
  const shown = alerts.filter((a) => !ui.filter || (ui.filter === 'Needs a look' ? a.tone === 'red' : ui.filter === 'Watch' ? a.tone === 'amber' : ui.filter === 'Noisy' ? noisyOf(a) : isAck(a)));
  const AL = alerts.find((a) => a.residentId === ui.sel) ?? shown[0] ?? alerts[0] ?? null;
  const base = {
    alGate: '● OBSERVATION, NOT DIAGNOSIS',
    alGateStyle: mark('ok'),
    alLede: 'Each one is worded as something a person could have noticed. The evidence sits under it, and nothing here names a condition.',
    alChips: ([['Needs a look', RED], ['Watch', AMBER], ['Noisy', MUTE], ['Acknowledged', OK]] as const).map((k) =>
      chip(k[0], k[0] === 'Needs a look' ? alerts.filter((a) => a.tone === 'red').length : k[0] === 'Watch' ? alerts.filter((a) => a.tone === 'amber').length : k[0] === 'Noisy' ? alerts.filter(noisyOf).length : alerts.filter(isAck).length,
        ui.filter === k[0], k[1], (e) => { pre(e); setUi({ filter: ui.filter === k[0] ? null : k[0], sel: null }); })),
    alList: shown.map((a) => {
      const noisy = noisyOf(a);
      const ack = isAck(a);
      const on = AL?.residentId === a.residentId;
      return {
        who: a.name + ' · ' + (a.room ?? '–'),
        what: a.observation,
        mark: ack ? '●' : a.tone === 'red' ? '▲' : a.tone === 'amber' ? '◆' : '○',
        markStyle: 'flex:0 0 auto;font-size:14px;color:' + (ack ? OK : TONE[a.tone] || MUTE) + ';',
        when: a.hoursAgo < 24 ? a.hoursAgo + 'h' : Math.round(a.hoursAgo / 24) + 'd',
        whenColor: a.tone === 'red' && !ack ? RED : MUTE,
        bars: a.fired14.map((v, i) =>
          'display:block;width:3px;border-radius:1px;height:' + (v ? (a.acted14[i] ? '12px' : '9px') : '3px') + ';background:' + (v ? (a.acted14[i] ? OK : AMBER) : 'rgba(34,39,46,.16)') +
          ';transform-origin:bottom;animation:growH .4s ' + ease + ' both;animation-delay:' + i * 22 + 'ms;'),
        noise: noisy ? '◆ ' + sumOf(a.fired14) + '×, ' + (sumOf(a.acted14) || 'none') + ' acted' : sumOf(a.fired14) + '× / 14d',
        noiseStyle: noisy ? mark('amber') : 'flex:0 0 auto;font-size:14px;color:' + MUTE + ';white-space:nowrap;',
        onClick: (e: Ev) => { pre(e); setUi({ sel: a.residentId }); },
        rowStyle: 'display:block;min-width:0;padding:9px 0 9px 9px;border-left:2px solid ' + (on ? BLUE : ack ? 'rgba(34,39,46,.14)' : TONE[a.tone] || 'rgba(34,39,46,.14)') +
          ';border-top:1px solid rgba(34,39,46,.12);text-decoration:none;color:' + (on ? BLUE : INK) + ';' + (ack ? 'opacity:.62;' : '') + 'transition:border-color .15s ' + ease + ';',
      };
    }),
    alEmpty: shown.length === 0,
    alEmptyColor: ui.filter ? INK : '#12503C',
    alEmptyTitle: ui.filter ? 'Nothing is ' + ui.filter.toLowerCase() + '.' : 'Nobody needs a look right now.',
    alEmptyNote: ui.filter ? 'Clear the filter to see the rest.' : 'A quiet week is a real result, not an empty list.',
  };
  if (!AL) {
    return { ...base, alTitle: 'No Stop and Watch signals', alState: '', alStateStyle: 'display:none;', alMeta: '', alObsColor: MUTE, alObservation: '', alNotDx: '', alEvOpen: false, alEvLabel: '', alEvToggle: () => {}, alDetectors: [], alTimeline: [], alNoisy: false, alNoisyNote: '', alSbarOpen: false, alSbarChip: '', alSbarChipStyle: 'display:none;', alSbar: [], alSbarSendLabel: '', alSbarSendStyle: 'display:none;', alSbarSend: () => {}, alSbarCancel: () => {}, alSbarHint: '', alActs: [], alHint: '', alRo: false, alRoLine: '' };
  }
  const ack = isAck(AL);
  const sent = isSent(AL);
  const sbar = [
    { k: 'S', v: AL.observation },
    { k: 'B', v: AL.name + ', room ' + (AL.room ?? '–') + '. ' + AL.medsNote + ' ' + (AL.diagnosis ?? 'No active diagnosis on file') + '.' },
    { k: 'A', v: AL.detectors.filter((d) => d.ok === false).map((d) => d.name.toLowerCase() + ' ' + d.why).join('; ') + '.' },
    { k: 'R', v: AL.tone === 'red' ? 'Please assess in person now and record vitals either way.' : 'Please look in on her today and note what you see.' },
  ];
  // A read-only viewer may read the pre-filled SBAR; the writes are not offered at all.
  // V9: read-only roles get no Compose SBAR, Acknowledge or Not useful, only a line saying so.
  const acts: Array<[string, string]> = opts.canWrite ? [[sent ? 'SBAR sent' : 'Compose SBAR', 'sbar'], [ack ? 'Acknowledged' : 'Acknowledge', 'ack'], ['Not useful', 'false']] : [];
  return {
    ...base,
    alTitle: AL.name + ' · room ' + (AL.room ?? '–'),
    alState: sent ? '● ESCALATED' : ack ? '● ACKNOWLEDGED' : AL.tone === 'red' ? '▲ NEEDS A LOOK' : AL.tone === 'amber' ? '◆ WATCH' : '○ LOGGED',
    alStateStyle: mark(sent ? 'blue' : ack ? 'ok' : AL.tone),
    alMeta: (AL.hoursAgo < 24 ? AL.hoursAgo + ' hours ago' : Math.round(AL.hoursAgo / 24) + ' days ago') + ' · ' + AL.medsNote,
    alObsColor: ack ? OK : TONE[AL.tone] || MUTE,
    alObservation: AL.observation,
    alNotDx: 'That is what the pattern looks like from outside. It is not a diagnosis, and nobody has examined her.',
    alEvOpen: ui.evOpen,
    alEvLabel: ui.evOpen ? 'Hide' : 'Show ' + AL.detectors.length,
    alEvToggle: (e: Ev) => { pre(e); setUi({ evOpen: !ui.evOpen }); },
    alDetectors: AL.detectors.map((d, i) => ({
      name: d.name,
      why: d.why,
      mark: d.ok === null ? '○' : d.ok ? '●' : '▲',
      markStyle: 'font-size:14px;color:' + (d.ok === null ? MUTE : d.ok ? OK : RED) + ';',
      val: d.value < 0 ? 'not assessed' : String(d.value),
      valColor: d.ok === null ? MUTE : d.ok ? OK : RED,
      fill: bar(d.value < 0 ? 0 : d.value, d.ok === null ? MUTE : d.ok ? OK : RED, i * 70),
    })),
    alTimeline: AL.timeline.map((t, i) => ({
      when: t.when,
      what: t.what,
      dot: 'flex:0 0 6px;width:6px;height:6px;border-radius:50%;align-self:center;background:' + (i === AL.timeline.length - 1 ? TONE[AL.tone] || MUTE : 'rgba(34,39,46,.28)') + ';' +
        (i === AL.timeline.length - 1 && AL.tone === 'red' ? 'animation:bl 1.6s ' + ease + ' infinite;' : ''),
    })),
    alNoisy: noisyOf(AL),
    alNoisyNote: 'It has fired ' + sumOf(AL.fired14) + ' times in a fortnight and somebody acted ' +
      (sumOf(AL.acted14) ? sumOf(AL.acted14) + ' time' + (sumOf(AL.acted14) === 1 ? '' : 's') : 'none') + '. An alert nobody acts on is a cost, not a save: it is what makes the next real one get missed.',
    alSbarOpen: ui.sbarFor === AL.residentId,
    alSbarChip: sent ? '● SENT' : '◆ NOT SENT YET',
    alSbarChipStyle: mark(sent ? 'ok' : 'amber'),
    alSbar: sbar,
    alSbarSendLabel: sent ? 'Sent to the on-call nurse' : 'Send it',
    alSbarSendStyle: 'flex:0 0 auto;display:inline-flex;align-items:center;height:29px;padding:0 13px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;' +
      (sent ? 'background:rgba(31,107,82,.12);color:' + OK + ';' : 'background:' + BLUE + ';color:#FCFAF4;'),
    alSbarSend: (e: Ev) => { pre(e); if (!sent && opts.canWrite) opts.send(AL, sbar); },
    alSbarCancel: (e: Ev) => { pre(e); setUi({ sbarFor: null }); },
    alSbarHint: 'Pre-filled from her chart. Read it before you send it: it goes out under your name and is sealed to the ledger.',
    alActs: acts.map((x) => ({
      label: x[0],
      onClick: (e: Ev) => {
        pre(e);
        if (x[1] === 'sbar') setUi({ sbarFor: ui.sbarFor === AL.residentId ? null : AL.residentId });
        else if (x[1] === 'ack') { if (!ack && opts.canWrite) opts.ack(AL); }
        else { if (opts.canWrite) opts.notUseful(AL); setUi({ filter: 'Noisy', sel: null }); }
      },
      style: 'flex:0 0 auto;display:inline-flex;align-items:center;height:29px;padding:0 12px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;' +
        (x[1] === 'sbar' ? (sent ? 'background:rgba(31,107,82,.12);color:' + OK + ';' : 'background:' + BLUE + ';color:#FCFAF4;')
          : x[1] === 'ack' ? (ack ? 'background:rgba(31,107,82,.12);color:' + OK + ';' : 'background:rgba(47,92,151,.10);color:#1D3F6B;')
            : 'background:rgba(179,38,30,.10);color:' + RED + ';'),
    })),
    alRo: !opts.canWrite,
    alRoLine: 'Read-only as ' + (opts.roleLabel ?? 'your role') + '. The building’s nurses act on alerts.',
    alHint: sent ? 'SBAR sent under your name and sealed to the Truth Ledger.' : ack ? 'Acknowledged. That records a person saw it, not that anything was done.' : 'Acknowledging records that a person saw it. Escalating is the SBAR.',
  };
}
